import { FormCard, FormItem, Input, Select } from '@lmc/ui-kit'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { ResidentBody, ResidentItem } from '@lmc/ts-common'
import { UpdateResidentReturnType } from 'hooks/useResident'
import ImportantContactForm from '../ImportantContactForm'
import { Spinner } from 'components/generic'
import { Navigate } from 'react-router-dom'
import { AddressForm } from '../../ResidentProfileScreen/screens/ResidentContactDetails/FormCards/Address/AddressForm'
import { Button, Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import { useQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import { useState } from 'react'
import { Form } from 'antd'
import useRequiredRule from './utils/useRequiredRule'

export type FormType = Pick<
  ResidentBody,
  'gpCode' | 'gpEmail' | 'gpName' | 'gpSurgeryName' | 'gpTelephone'
> & {
  address: ResidentBody['gpAddress']
}

// move to ts-common
type GeneralPractitioner = Pick<
  ResidentBody,
  | 'gpCode'
  | 'gpEmail'
  | 'gpName'
  | 'gpSurgeryName'
  | 'gpTelephone'
  | 'gpAddress'
>

type GPContactFormProps = {
  onSubmit: (values: Partial<ResidentBody>) => Promise<UpdateResidentReturnType>
  resident: ResidentItem | undefined
  isLoading: boolean
  testId?: string
}

const GPContactForm = ({
  onSubmit,
  resident,
  isLoading,
  testId,
}: GPContactFormProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const [showGPList, setShowGPList] = useState(false)
  const requiredRule = useRequiredRule()

  const { data } = useQuery<{ result: GeneralPractitioner[] }>(['gps'], () =>
    getJSON({ url: '/api/list/gps' }),
  )

  const gps = data?.result
  const hasGPs = gps && gps.length

  if (isLoading) {
    return <Spinner />
  }

  if (!resident) {
    return <Navigate to="/residents" />
  }

  const initialValues: Partial<FormType> = {
    address: resident.gpAddress ? resident.gpAddress : undefined,
    gpCode: resident.gpCode,
    gpEmail: resident.gpEmail,
    gpName: resident.gpName,
    gpSurgeryName: resident.gpSurgeryName,
    gpTelephone: resident.gpTelephone,
  }

  const handleSubmit = (
    values: FormType,
  ): Promise<UpdateResidentReturnType> => {
    const convertedValues: Partial<ResidentBody> = {
      gpAddress: values.address,
      gpCode: values.gpCode,
      gpEmail: values.gpEmail,
      gpName: values.gpName,
      gpSurgeryName: values.gpSurgeryName,
      gpTelephone: values.gpTelephone,
    }

    return onSubmit(convertedValues)
  }

  const toggleSelectGP = (): void => {
    setShowGPList((prevState) => !prevState)
  }
  const residentName = `${resident.name.first} ${resident.name.last}`

  return (
    <ImportantContactForm<FormType>
      updateResident={handleSubmit}
      title={`${residentName}'s ${t(supConstants.GP_DETAILS)}`}
      initialValues={initialValues}
      onDelete={undefined}
      testId={testId}
    >
      <FormCard title={t(supConstants.GENERAL_INFORMATION)} testId={testId}>
        <FormItemWrapper>
          <FormItem
            required
            rules={[requiredRule(t(supConstants.DOCTOR_NAME))]}
            label={t(supConstants.DOCTOR_NAME)}
            name="gpName"
          >
            {showGPList ? (
              <Form.Item noStyle shouldUpdate={() => showGPList}>
                {({ setFieldsValue, getFieldValue }) => (
                  <Form.Item>
                    <Select
                      value={getFieldValue('gpName')}
                      options={gps?.map((gp) => ({
                        label: gp.gpName,
                        value: gp.gpName,
                        gp,
                      }))}
                      onChange={(value, option) => {
                        if (Array.isArray(option)) {
                          return
                        }

                        const formValues = convertGPToFormType(option.gp)

                        setFieldsValue(formValues)
                      }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            ) : (
              <Input />
            )}
          </FormItem>
          {hasGPs && (
            <Button variant="text" onClick={toggleSelectGP} type="button">
              {showGPList
                ? t(supConstants.TYPE_MANUALLY)
                : t(supConstants.CHOOSE_GP_FROM_LIST)}
            </Button>
          )}
        </FormItemWrapper>
        <FormItem
          rules={[
            {
              validator: (props, value: string) => getGpCodeValidator(value, t),
            },
          ]}
          label={t(supConstants.GP_PRACTICE_CODE)}
          name="gpCode"
        >
          <Input />
        </FormItem>
        <FormItem
          label={t(supConstants.SURGERY_PRACTICE_NAME)}
          name="gpSurgeryName"
        >
          <Input />
        </FormItem>
      </FormCard>
      <FormCard title={t(supConstants.CONTACT_DETAILS)}>
        <Form.Item shouldUpdate={() => false}>
          {({ getFieldValue, setFieldValue }) => {
            const isAddressFilled = !!getFieldValue(['address'])
            return (
              <AddressForm
                initialIsAddingAddressManually={isAddressFilled}
                fieldName={['address']}
                onAutofill={(values) => setFieldValue('address', values)}
                title={t(supConstants.ADDRESS)}
              >
                <FormItem
                  label={<Text>{t(supConstants.BUILDING_NAME)}</Text>}
                  name={['address', 'name']}
                >
                  <Input />
                </FormItem>
                <FormItem
                  label={<Text>{t(supConstants.ADDRESS_LINE_1)}</Text>}
                  name={['address', 'street1']}
                >
                  <Input />
                </FormItem>
                <FormItem
                  label={<Text>{t(supConstants.ADDRESS_LINE_2)}</Text>}
                  name={['address', 'street2']}
                >
                  <Input />
                </FormItem>
                <FormItem
                  label={<Text>{t(supConstants.TOWN_CITY)}</Text>}
                  name={['address', 'suburb']}
                >
                  <Input />
                </FormItem>
                <FormItem
                  label={<Text>{t(supConstants.COUNTRY)}</Text>}
                  name={['address', 'state']}
                >
                  <Input />
                </FormItem>
              </AddressForm>
            )
          }}
        </Form.Item>

        <FormItem label={t(supConstants.EMAIL)} name="gpEmail">
          <Input name="gpEmail" />
        </FormItem>
        <FormItem label={t(supConstants.PHONE_NO)} name="gpTelephone">
          <Input />
        </FormItem>
      </FormCard>
    </ImportantContactForm>
  )
}

const getGpCodeValidator = async (
  value: string,
  t: (value: string) => string,
) => {
  if (!value) {
    return
  }

  const scotlandRegex = /^\d{6}$/
  const englandWalesRegex = /^V\d{5}$|^G\d{7}$/

  // Check for Scotland
  if (scotlandRegex.test(value)) {
    return
  }

  // Check for England and Wales
  if (englandWalesRegex.test(value)) {
    return
  }

  throw new Error(t(supConstants.GP_CODE_VALIDATION_ERROR))
}

const convertGPToFormType = (gp: GeneralPractitioner): FormType => ({
  address: gp.gpAddress,
  gpCode: gp.gpCode,
  gpEmail: gp.gpEmail,
  gpName: gp.gpName,
  gpSurgeryName: gp.gpSurgeryName,
  gpTelephone: gp.gpTelephone,
})

const FormItemWrapper = styled.div`
  display: flex;
  align-items: center;

  gap: 16px;
`

export default GPContactForm
